<div class="activity-booking-modal">
    <!-- Dialog Header -->
    <div class="modal-header px-4 py-2">
        <div class="modal-title">
            <mat-icon>{{ getModalIcon() }}</mat-icon>
            <span>{{ getModalTitle() }}</span>
        </div>
        <button mat-icon-button (click)="onCancel()" matTooltip="Închide">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <!-- Main Content -->
    <mat-dialog-content class="dialog-content">
        <form [formGroup]="bookingForm" class="booking-form">

            <!-- Conflict Resolution Alert -->
            <mat-card class="conflict-alert mb-2" *ngIf="showConflictResolution" appearance="outlined">
                <mat-card-header>
                    <mat-icon color="warn">warning</mat-icon>
                    <mat-card-title>Conflict detectat</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <p>{{ getConflictMessage() }}</p>

                    <!-- Suggested Times -->
                    <div class="suggested-times" *ngIf="suggestedTimes.length > 0">
                        <h4>Ore sugerate:</h4>
                        <div class="time-suggestions">
                            <button *ngFor="let time of suggestedTimes" type="button" mat-chip-button
                                (click)="applySuggestedTime(time)" class="time-suggestion">
                                {{ time }}
                            </button>
                        </div>
                    </div>

                    <!-- Conflict Actions -->
                    <div class="conflict-actions">
                        <button type="button" mat-stroked-button (click)="forceBooking()">
                            <mat-icon>priority_high</mat-icon>
                            Forțează rezervarea
                        </button>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Basic Information Section -->
            <mat-card class="form-section mb-2">
                <mat-card-header class="mb-3">
                    <mat-card-title class="d-flex align-items-center gap-2">
                        <mat-icon>info</mat-icon>
                        Informații de bază
                    </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="form-grid">
                        <!-- Activity Type -->
                        <mat-form-field appearance="outline" class="form-field">
                            <mat-label>Tip activitate</mat-label>
                            <mat-select formControlName="activityTypeId" required>
                                <mat-option *ngFor="let type of activityTypes" [value]="type.id">
                                    {{ type.name }}
                                </mat-option>
                            </mat-select>
                            <mat-error>{{ getFieldError('activityTypeId') }}</mat-error>
                        </mat-form-field>

                        <!-- Title -->
                        <mat-form-field appearance="outline" class="form-field full-width">
                            <mat-label>Titlu activitate</mat-label>
                            <input matInput formControlName="title" required maxlength="255">
                            <mat-hint>{{ bookingForm.get('title')?.value?.length || 0 }}/255</mat-hint>
                            <mat-error>{{ getFieldError('title') }}</mat-error>
                        </mat-form-field>

                        <!-- Description -->
                        <mat-form-field appearance="outline" class="form-field full-width">
                            <mat-label>Descriere (opțional)</mat-label>
                            <textarea matInput formControlName="description" rows="3" maxlength="1000"></textarea>
                            <mat-hint>{{ bookingForm.get('description')?.value?.length || 0 }}/1000</mat-hint>
                            <mat-error>{{ getFieldError('description') }}</mat-error>
                        </mat-form-field>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Date and Time Section -->
            <mat-card class="form-section mb-2">
                <mat-card-header class="mb-3">
                    <mat-card-title class="d-flex align-items-center gap-2">
                        <mat-icon>schedule</mat-icon>
                        Data și ora
                        <mat-icon *ngIf="isCheckingAvailability" class="availability-spinner">
                            <mat-spinner diameter="16"></mat-spinner>
                        </mat-icon>
                    </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="form-grid">
                        <!-- Date -->
                        <mat-form-field appearance="outline" class="form-field">
                            <mat-label>Data</mat-label>
                            <input matInput [matDatepicker]="datePicker" formControlName="date" [min]="minDate"
                                [max]="maxDate" required>
                            <mat-datepicker-toggle matSuffix [for]="datePicker"></mat-datepicker-toggle>
                            <mat-datepicker #datePicker></mat-datepicker>
                            <mat-error>{{ getFieldError('date') }}</mat-error>
                        </mat-form-field>

                        <!-- Start Time -->
                        <mat-form-field appearance="outline" class="form-field">
                            <mat-label>Ora început</mat-label>
                            <mat-select formControlName="startTime" required>
                                <mat-option *ngFor="let time of timeSlots" [value]="time">
                                    {{ time }}
                                </mat-option>
                            </mat-select>
                            <mat-error>{{ getFieldError('startTime') }}</mat-error>
                        </mat-form-field>

                        <!-- Duration -->
                        <mat-form-field appearance="outline" class="form-field">
                            <mat-label>Durata</mat-label>
                            <mat-select formControlName="duration" required>
                                <mat-option *ngFor="let option of durationOptions" [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            </mat-select>
                            <mat-error>{{ getFieldError('duration') }}</mat-error>
                        </mat-form-field>

                        <!-- End Time (readonly) -->
                        <mat-form-field appearance="outline" class="form-field">
                            <mat-label>Ora sfârșit</mat-label>
                            <input matInput formControlName="endTime" readonly>
                            <mat-icon matSuffix>lock</mat-icon>
                        </mat-form-field>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Location Section -->
            <mat-card class="form-section mb-2">
                <mat-card-header class="mb-3">
                    <mat-card-title class="d-flex align-items-center gap-2">
                        <mat-icon>place</mat-icon>
                        Locație
                    </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="form-grid">
                        <!-- Online Meeting Toggle -->
                        <div class="form-field full-width mb-3">
                            <mat-slide-toggle formControlName="isOnline" color="primary">
                                Întâlnire online
                            </mat-slide-toggle>
                        </div>

                        <!-- Online Link -->
                        <mat-form-field appearance="outline" class="form-field full-width"
                            *ngIf="bookingForm.get('isOnline')?.value">
                            <mat-label>Link întâlnire</mat-label>
                            <input matInput formControlName="onlineLink"
                                placeholder="https://meet.google.com/abc-defg-hij">
                            <mat-icon matSuffix>link</mat-icon>
                            <mat-error>{{ getFieldError('onlineLink') }}</mat-error>
                        </mat-form-field>

                        <!-- Physical Location -->
                        <mat-form-field appearance="outline" class="form-field full-width"
                            *ngIf="!bookingForm.get('isOnline')?.value">
                            <mat-label>Locația fizică</mat-label>
                            <input matInput formControlName="location"
                                placeholder="ex: Biroul 101, Strada Exemplu nr. 1">
                            <mat-icon matSuffix>location_on</mat-icon>
                            <mat-error>{{ getFieldError('location') }}</mat-error>
                        </mat-form-field>
                    </div>
                </mat-card-content>
            </mat-card>

            <!-- Advanced Options -->
            <mat-expansion-panel class="advanced-options" [(expanded)]="showAdvancedOptions" hideToggle="false">
                <mat-expansion-panel-header>
                    <mat-panel-title>
                        <mat-icon>tune</mat-icon>
                        Opțiuni avansate
                    </mat-panel-title>
                </mat-expansion-panel-header>

                <div class="advanced-content">
                    <!-- Priority -->
                    <mat-form-field appearance="outline" class="form-field">
                        <mat-label>Prioritate</mat-label>
                        <mat-select formControlName="priority">
                            <mat-option *ngFor="let option of priorityOptions" [value]="option.value">
                                <mat-chip [style.background-color]="option.color" class="priority-chip">
                                    {{ option.label }}
                                </mat-chip>
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <!-- Settings Toggles -->
                    <div class="settings-toggles d-flex flex-column gap-3 mb-3">
                        <mat-slide-toggle formControlName="isPrivate" color="primary">
                            Activitate privată
                        </mat-slide-toggle>

                        <mat-slide-toggle formControlName="sendNotifications" color="primary">
                            Trimite notificări
                        </mat-slide-toggle>

                        <mat-slide-toggle formControlName="allowConflicts" color="warn">
                            Permite conflicte de programare
                        </mat-slide-toggle>
                    </div>

                    <!-- Notes -->
                    <mat-form-field appearance="outline" class="form-field full-width">
                        <mat-label>Note interne</mat-label>
                        <textarea matInput formControlName="internalNotes" rows="2" maxlength="500"></textarea>
                        <mat-hint>{{ bookingForm.get('internalNotes')?.value?.length || 0 }}/500</mat-hint>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="form-field full-width mt-2">
                        <mat-label>Note pentru participanți</mat-label>
                        <textarea matInput formControlName="participantNotes" rows="2" maxlength="500"></textarea>
                        <mat-hint>{{ bookingForm.get('participantNotes')?.value?.length || 0 }}/500</mat-hint>
                    </mat-form-field>
                </div>
            </mat-expansion-panel>
        </form>
    </mat-dialog-content>

    <!-- Dialog Actions -->
    <mat-dialog-actions align="end" class="dialog-actions">
        <button mat-stroked-button (click)="onCancel()" [disabled]="isSaving">
            Anulează
        </button>

        <button mat-raised-button color="primary" (click)="onSave()"
            [disabled]="!bookingForm.valid || isSaving || (hasConflicts() && !bookingForm.get('allowConflicts')?.value)">
            <mat-icon *ngIf="!isSaving">{{ mode === 'edit' ? 'save' : 'add' }}</mat-icon>
            <mat-spinner *ngIf="isSaving" diameter="16"></mat-spinner>
            {{ getSubmitButtonText() }}
        </button>
    </mat-dialog-actions>

    <!-- Loading Overlay -->
    <div *ngIf="isLoading" class="loading-overlay">
        <mat-spinner></mat-spinner>
        <p>Se încarcă...</p>
    </div>
</div>